import { useCallback, useEffect, useRef, useState } from "react";
import { pickRecorderMimeType, voiceFileFromChunks } from "./voiceRecording";

const WAVE_BAR_COUNT = 40;
const IDLE_LEVELS = () => Array.from({ length: WAVE_BAR_COUNT }, () => 0.12);

const formatDuration = (ms) => {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

/**
 * MediaRecorder + AnalyserNode waveform + pause-aware timer for voice notes.
 */
export default function useVoiceRecorder() {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [durationMs, setDurationMs] = useState(0);
  const [levels, setLevels] = useState(IDLE_LEVELS);
  const [error, setError] = useState("");

  const mediaRecorderRef = useRef(null);
  const mediaChunksRef = useRef([]);
  const mediaStreamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const mimeTypeRef = useRef("");
  const startedAtRef = useRef(0);
  const pausedAccumRef = useRef(0);
  const pauseStartedAtRef = useRef(0);
  const discardOnStopRef = useRef(false);
  const resolveStopRef = useRef(null);
  const startingRef = useRef(false);

  const stopTracks = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  const stopAnalyser = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    analyserRef.current = null;
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
  }, []);

  const tickTimerAndWave = useCallback(() => {
    const update = () => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === "inactive") return;

      if (recorder.state === "recording") {
        const elapsed =
          pausedAccumRef.current + (Date.now() - startedAtRef.current);
        setDurationMs(elapsed);

        const analyser = analyserRef.current;
        if (analyser) {
          const data = new Uint8Array(analyser.frequencyBinCount);
          analyser.getByteFrequencyData(data);
          const next = [];
          const step = Math.max(1, Math.floor(data.length / WAVE_BAR_COUNT));
          for (let i = 0; i < WAVE_BAR_COUNT; i += 1) {
            let sum = 0;
            const start = i * step;
            for (let j = 0; j < step && start + j < data.length; j += 1) {
              sum += data[start + j];
            }
            const avg = sum / step / 255;
            next.push(Math.max(0.08, Math.min(1, avg * 1.6)));
          }
          setLevels(next);
        }
      }

      rafRef.current = requestAnimationFrame(update);
    };
    rafRef.current = requestAnimationFrame(update);
  }, []);

  const resetUi = useCallback(() => {
    setIsRecording(false);
    setIsPaused(false);
    setDurationMs(0);
    setLevels(IDLE_LEVELS());
  }, []);

  const start = useCallback(async () => {
    if (isRecording || startingRef.current) return false;
    startingRef.current = true;
    setError("");

    if (
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === "undefined"
    ) {
      setError("unsupported");
      startingRef.current = false;
      return false;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      mediaChunksRef.current = [];
      discardOnStopRef.current = false;

      const mimeType = pickRecorderMimeType();
      mimeTypeRef.current = mimeType;
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          mediaChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        stopAnalyser();
        stopTracks();
        const chunks = mediaChunksRef.current;
        mediaChunksRef.current = [];
        mediaRecorderRef.current = null;
        startingRef.current = false;
        const shouldDiscard = discardOnStopRef.current;
        discardOnStopRef.current = false;
        resetUi();

        const resolve = resolveStopRef.current;
        resolveStopRef.current = null;

        if (shouldDiscard || !chunks.length) {
          resolve?.(null);
          return;
        }

        const file = voiceFileFromChunks(
          chunks,
          recorder.mimeType || mimeTypeRef.current || "audio/webm",
        );
        resolve?.(file);
      };

      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx) {
          const ctx = new AudioCtx();
          audioCtxRef.current = ctx;
          const source = ctx.createMediaStreamSource(stream);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 256;
          analyser.smoothingTimeConstant = 0.75;
          source.connect(analyser);
          analyserRef.current = analyser;
          if (ctx.state === "suspended") {
            ctx.resume().catch(() => {});
          }
        }
      } catch {
        /* waveform optional */
      }

      pausedAccumRef.current = 0;
      pauseStartedAtRef.current = 0;
      startedAtRef.current = Date.now();
      setDurationMs(0);
      setIsPaused(false);
      setIsRecording(true);

      // Timeslice helps iOS flush audio chunks reliably before stop()
      recorder.start(250);
      tickTimerAndWave();
      startingRef.current = false;
      return true;
    } catch {
      startingRef.current = false;
      stopAnalyser();
      stopTracks();
      resetUi();
      setError("permission");
      return false;
    }
  }, [isRecording, resetUi, stopAnalyser, stopTracks, tickTimerAndWave]);

  const stop = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      resetUi();
      stopAnalyser();
      stopTracks();
      return Promise.resolve(null);
    }

    discardOnStopRef.current = false;
    return new Promise((resolve) => {
      resolveStopRef.current = resolve;
      try {
        if (recorder.state === "paused") {
          recorder.resume();
        }
        recorder.stop();
      } catch {
        resolveStopRef.current = null;
        stopAnalyser();
        stopTracks();
        resetUi();
        resolve(null);
      }
    });
  }, [resetUi, stopAnalyser, stopTracks]);

  const cancel = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    discardOnStopRef.current = true;
    mediaChunksRef.current = [];

    if (!recorder || recorder.state === "inactive") {
      stopAnalyser();
      stopTracks();
      resetUi();
      return;
    }

    try {
      recorder.ondataavailable = null;
      if (recorder.state === "paused") {
        recorder.resume();
      }
      recorder.stop();
    } catch {
      stopAnalyser();
      stopTracks();
      resetUi();
    }
  }, [resetUi, stopAnalyser, stopTracks]);

  const togglePause = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;

    try {
      if (recorder.state === "recording") {
        recorder.pause();
        pausedAccumRef.current += Date.now() - startedAtRef.current;
        pauseStartedAtRef.current = Date.now();
        setIsPaused(true);
      } else if (recorder.state === "paused") {
        recorder.resume();
        startedAtRef.current = Date.now();
        pauseStartedAtRef.current = 0;
        setIsPaused(false);
      }
    } catch {
      /* pause not supported */
    }
  }, []);

  const clearError = useCallback(() => setError(""), []);

  useEffect(() => {
    return () => {
      discardOnStopRef.current = true;
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        try {
          recorder.ondataavailable = null;
          recorder.onstop = null;
          recorder.stop();
        } catch {
          /* ignore */
        }
      }
      stopAnalyser();
      stopTracks();
    };
  }, [stopAnalyser, stopTracks]);

  return {
    isRecording,
    isPaused,
    durationMs,
    durationLabel: formatDuration(durationMs),
    levels,
    error,
    clearError,
    start,
    stop,
    cancel,
    togglePause,
  };
}
